import {useContext, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';

import Page from '../../../layout/Page/Page';
import PageRow from '../../../layout/Page/PageRow';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Logo from '../../../components/Logo';
import Button from '../../../components/bootstrap/Button';
import Dropdown, {DropdownItem, DropdownMenu, DropdownToggle} from '../../../components/bootstrap/Dropdown';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import InputGroup from '../../../components/bootstrap/forms/InputGroup';
import Label from '../../../components/bootstrap/forms/Label';
import Select from '../../../components/bootstrap/forms/Select';
import Icon from '../../../components/icon/Icon';
import showNotification from '../../../components/extras/showNotification';
import ThemeContext from '../../../contexts/themeContext';
import {useChangeLanguage} from '../../../helpers/locales';
import LANG, {getLangWithKey} from '../../../lang';
import {pawsswordFind} from '../../../menu';
import {Configs} from '../../../models/Configs';
import {useMst} from '../../../models';
import AuthService from '../../../services/AuthService';
import BoardService from '../../../services/BoardService';
import useDarkMode from '../../../hooks/useDarkMode';
import config from '../../../config';
import {generateNonce, generateState} from '../../../lib/Util';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';

/******************************************************************
 * 파일명 : frontend2\src\pages\CommonMenu\Login\LoginNew.js
 * 메뉴 : 로그인 페이지
 * URL  : /login
 ******************************************************************/

const Login = () => {
	const { updateLanguage } = useChangeLanguage();
	const { darkModeStatus } = useDarkMode();
	const { t, i18n } = useTranslation();
	const { user, company } = useMst();
	const { setNoticeCount, lang, mobileDesign } = useContext(ThemeContext);
	const [visiblePW, setVisiblePW] = useState(false);
	const [isPolicyModal, setIsPolicyModal] = useState(false);

	const loginData = useFormik({
		initialValues: {
			email: '',
			password: '',
		},
		onSubmit: (values) => {
			if (!values.email || !values.password) {
				showNotification(t('login.login'), t('login.checkEmailAndPass'), 'danger');
			} else {
				requestLogin(values);
			}
		},
	});

	// login.login 시 읽지 않은 게시물 카운트
	const readNoticeCount = async () => {
		const noticeCount = await BoardService.readCount('notice', user?.me?.currentCompanyId);
		setNoticeCount(noticeCount?.data || 0);
	};

	const isAppEnvironment = window.flutter_inappwebview && typeof window.flutter_inappwebview.callHandler === 'function'; // 웹뷰인지
	const uAgent = navigator.userAgent;
	const isAndroid = isAppEnvironment && /Android/i.test(uAgent);     // 안드로이드인지
	const isIOS     = isAppEnvironment && /iPhone|iPad/i.test(uAgent); // iOS인지

	const requestLogin = async (values) => {
		try {
			const loginResult = await AuthService.login(values);

			if (loginResult?.result) {
				try {
					await user.authMe();
					await Promise.all([
						Configs.sendDevice(),
						// readNoticeCount(),
						user?.me?.currentCompanyId && company.selectCompany(user?.me?.currentCompanyId, mobileDesign),
					]);
					if (user.me.id) {
						//웹뷰 환경
						if (isAppEnvironment) {

							// OS에 맞는 알림 문구 세팅 (안드로이드 = 지문, iOS = Face ID)
							let bioTypeName = '';
							if (isAndroid) {
								bioTypeName = t('login.bio.fingerprint');
							} else if (isIOS) {
								bioTypeName = t('login.bio.faceId');
							}

							// 안드로이드 또는 iOS이고, 이 폰에 지금 로그인한 이 계정의 정보가 아직 없을 때만 등록 유도
							// (email까지 같이 넘겨서 비교 -> 같은 폰에서 다른 계정으로 로그인해도 정상적으로 재유도됨)
							if (isAndroid || isIOS) {
								let hasSavedCredentials = false;
								try {
									const savedResult = await window.flutter_inappwebview.callHandler('HasSavedCredentials', values.email);
									hasSavedCredentials = !!savedResult?.hasSavedCredentials;
								} catch (error) {
									console.error('저장된 계정 정보 확인 실패:', error);
									hasSavedCredentials = true;
								}

								if (!hasSavedCredentials) {
									const nextLoginBioConfirm = window.confirm(t('login.bio.confirmMessage', { type: bioTypeName }));

									if (nextLoginBioConfirm) {
										try {
											// Flutter의 BiometricDeviceInfo 채널 호출 (지문/Face ID 팝업으로 실제 등록 의사 재확인)
											const deviceInfo = await window.flutter_inappwebview.callHandler('BiometricDeviceInfo');
											console.log("deviceInfo: ", deviceInfo);

											if (deviceInfo && deviceInfo.success) {
												// 지문 스캔 성공 시에만 Flutter로 계정 정보를 전달해 다음 로그인용으로 저장하게 함
												await window.flutter_inappwebview.callHandler('ToFlutter', JSON.stringify({
													type: 'login_success',
													email: values.email,
													password: values.password
												}));
												console.log("Flutter로(메타타임 앱) 로그인 정보 전달 완료!");

												showNotification(t('login.bio.registerTitle', { type: bioTypeName }), t('login.bio.registerSuccess', { type: bioTypeName }), 'success');
											} else {
												showNotification(t('login.bio.registerTitle', { type: bioTypeName }), t('login.bio.registerCanceled', { type: bioTypeName }), 'danger');
											}
										} catch (error) {
											console.error(`${bioTypeName} 등록 중 에러:`, error);
											showNotification(t('login.bio.registerTitle', { type: bioTypeName }), t('login.bio.registerError'), 'danger');
										}
									}
								}
							}
						}

						if (!user?.me?.currentCompanyId) {
							return window.location.replace('/change-company');
						} else {
							return window.location.replace('/home');
						}
					}
				} catch (error) {
					showNotification(t('login.login'), t('로그인에 실패하였습니다.') + t('다시 확인해주세요') + '.', 'danger');
				}
			} else {
				showNotification(t('login.login'), t(loginResult?.message), 'danger');
			}
		} catch (error) {
			showNotification(t('login.login'), t(error?.message), 'danger');
		}
	};

	const handleBiometricAuth = async () => {
		if (isAppEnvironment) {
			try {
				console.log("Flutter에 저장된 계정 정보 기반 지문 인증 로그인 요청 중...");
				// 저장된 계정 정보 확인 + 지문 스캔까지 Flutter가 처리하고,
				// 성공 시 Flutter가 직접 window.requestLogin(email, password)을 호출함(main.dart의 자동 로그인과 동일 방식)
				const result = await window.flutter_inappwebview.callHandler('BiometricAutoLogin');
				console.log('result', result);

				if (!result?.success) {
					if (result?.reason === 'no_saved_credentials') {
						showNotification(t('login.login'), t('login.bio.deviceNotRegistered'), 'danger');
					} else {
						showNotification(t('login.login'), t('login.bio.authFailed'), 'danger');
					}
				}
				// success:true인 경우, Flutter가 이미 requestLogin을 호출했으므로 여기선 할 일 없음
				// (requestLogin 자체의 성공/실패 알림은 requestLogin 내부에서 처리됨)
			} catch (e) {
				console.error("생체 인증 웹뷰 브릿지 호출 에러: ", e);
			}
		} else {
			console.log('WebView 환경이 아님');
		}
	}

	useEffect(() => {
		console.log('useEffect window.requestLogin 시작');
		if (requestLogin) {
			window.requestLogin = requestLogin;
			console.log('window.requestLogin 등록 완료');
		} else {
			console.error('requestLogin 함수 정의되어있지 않음');
		}
		return()=>{
			// console.log('--- window.requestLogin 삭제 ---');
			// delete window.requestLogin;
		}
	}, []);

	const openAzureLoginPage = () => {
		const nonce = generateNonce();

		window.location.href = `https://login.microsoftonline.com/${
			config.AZURE_TENANT
		}/oauth2/v2.0/authorize?client_id=${
			config.AZURE_CLIENT_ID
		}&redirect_uri=${
			encodeURIComponent(config.AZURE_REDIRECT_URI)
		}&response_type=code&scope=User.Read&nonce=${nonce}`
	};

	const langOptions = Object.keys(LANG).map((i) => ({
		value: LANG[i].lng,
		text: LANG[i].text,
	}));

	return (
		<PageWrapper title={'Login'} className='p-0 flex-row login_wrap'>
			{!mobileDesign && (
				<PageRow className='login-bg col-md-7 p-0'>
					<div className='txt_box'>
						<h1>{t('login.sentence1')}</h1>
						<p>{t('login.sentence2')}</p>
						<span><span>{t('login.sentence3')}</span> <br/> <span>{t('login.sentence4')}</span></span>
					</div>
				</PageRow>
			)}
			<Page className={`col-xl-5 p-0 d-flex justify-content-center ${mobileDesign ? '' : 'align-items-center'}`}>
				<div className='col-xl-6 col-lg-6 login_box'>
					<div className='text-center'>
						<Logo width={150} />
					</div>
					<form onSubmit={loginData.handleSubmit} className='login_form'>
						<div className='id_box'>
							<Label htmlFor='email'><span className='visually-hidden'>{t('login.id')}</span></Label>
							<Input
								id='email'
								type='text'
								value={loginData.values.email}
								placeholder={t('login.id')}
								onChange={loginData.handleChange}
							/>
						</div>
						<div className='pw_box'>
							<Label htmlFor='password'><span className='visually-hidden'>{t('login.password')}</span></Label>
							<Input
								id='password'
								type={visiblePW ? 'text' : 'password'}
								value={loginData.values.password}
								placeholder={t('login.password')}
								onChange={loginData.handleChange}
							/>
							<Button
								className={`btn_visibility ${visiblePW ? 'active' : ''}`}
								onClick={() => setVisiblePW(!visiblePW)}
								aria-label={visiblePW ? t('login.showPassword') : t('login.hidePassword')}
							/>
						</div>
						<div className='btn_login_box'>
							<Button
								id='login-btn'
								className='btn_login'
								type='submit'
								onClick={() => {
									if (!loginData.isValid) {
										showNotification(t('login.login'), t('login.invalidEmailOrPassword'), 'danger');
									}
								}}
							>
								{t('login.login')}
							</Button>

							{(isAndroid || isIOS) && ( // 안드로이드나, iOS 환경일 때
								<Button
									className='btn_faceid'
									type='button'
									onClick={handleBiometricAuth}
								>
									<Icon // 안드로이드 = 지문, iOS = Face ID
										icon={isAndroid ? 'Fingerprint' : 'FaceId'}
										className='me-2'
									/>
									{isAndroid ? t('login.bio.fingerprintLoginButton') : t('login.bio.faceIdLoginButton')}
								</Button>
							)}

							{/* pw 초기화 + 개인정보처리방침 */}
							<div className='util_box'>
								<Button
									tag='a'
									to={'/' + pawsswordFind.PasswdReset.path}
								>
									{t('login.resetPass')}
								</Button>

								<Button
									type='button'
									onClick={() => setIsPolicyModal(true)}
								>
									{t('login.privacyPolicy')}
								</Button>

								<Select
									id='selectLang'
									className='select_lang'
									ariaLabel={t('언어 설정')}
									value={i18n.language}
									onChange={(e) => updateLanguage(e.target.value)}
									list={langOptions}
								/>
							</div>

							{/* sso 로그인 */}
							{config.SSO_PROVIDER === 'AZURE' && !mobileDesign && (
								<Button
									className='btn_sso'
									onClick={openAzureLoginPage}
								>
									{t('login.azureLogin')}
								</Button>
							)}
						</div>
					</form>
				</div>
			</Page>

			{/* 개인정보처리방침 모달 => 내용이 길어서 컴포넌트로 분리 */}
			<PrivacyPolicyModal isPolicyModal={isPolicyModal} setIsPolicyModal={setIsPolicyModal}/>
		</PageWrapper>
	);
};
export default Login;
